import { useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { C } from '@ridgewill/brand';
import { Select } from '../components/Select.js';
import type { RunJob, RunsFeed } from '../lib/fleet-api.js';
import {
  countTicks,
  durationLabel,
  filterJobs,
  filterOptions,
  focusRange,
  inWindow,
  jobTime,
  jobUrl,
  NO_FILTER,
  OUTCOMES,
  outcomeOf,
  RANGES,
  rangeOf,
  scatterAxis,
  slotsFor,
  type Outcome,
  type Range,
  type RunFilter,
  type Slot,
} from '../lib/fleet-runs.js';
import { seconds } from '../lib/fleet-view.js';
import { useAsync } from '../lib/useAsync.js';
import { count, numeric } from '../lib/format.js';
import { EmptyState, Note, Panel, Table, Td } from '../components/ui.js';
import { Menu, MenuItem } from '../components/Screen.js';
import { COLOR, JobDetails, KIND_LABEL, labelOf, OutcomeLabel, RepoBranch } from './JobDetails.js';

/**
 * Run history: the CI fleet screen's Run history tab. RidgeWill/org-admin#111.
 *
 * Jon, 13 Sep, on the daily-median line this replaces: it "doesn't provide much
 * useful information. Comparatively, Blacksmith provides a histogram or
 * scatterplot for job run history. I like that better."
 *
 * ── ONE AXIS PER CHART, UNLIKE BLACKSMITH ────────────────────────────
 *
 * Blacksmith draws run counts and average duration on one plot with two
 * y-scales. Where two scales happen to line up is arbitrary, so that shape makes
 * any two measures look related. Here the counts are the bars and the durations
 * are the scatter, each on one axis, toggled the way Blacksmith toggles; a
 * slot's median run time is in its tooltip.
 *
 * ── THE CHARTS ARE WAYS IN, NOT JUST PICTURES ────────────────────────
 *
 * Clicking a bar narrows everything on the tab to that slot, shown as a
 * removable tag in the filter row; clicking a dot opens that job's details.
 * The tooltip sits just above the pointer, portalled to the body so no panel
 * clips it, and never over the mark it describes (Jon, 13 Sep).
 *
 * ── THE LIST, AND ITS DETAILS DIALOG ─────────────────────────────────
 *
 * The recent-runs list is the table twin of the charts: outcome, job, repo and
 * branch, wait and run time. Everything else about a job (trigger, runner,
 * timestamps, ids, the GitHub link) is one double-click, a dot click, or the
 * row's menu away, in a dialog. `Modal.tsx` reserves dialogs for confirmations
 * and pickers, because a RECORD should be a location; a job is not a record
 * this portal owns, has no screen of its own, and Jon asked for the dialog.
 */

type View = 'bars' | 'scatter';

/** The hovered dot. Brand blue is free on the scatter: running jobs, the only other blue, are never plotted there. */
const HIGHLIGHT = C.brand;

/**
 * The longest a Workflows menu entry is drawn, in characters; the full name is
 * its hover text. A native menu is as wide as its longest entry, and before
 * fleet-api#41 Dependabot's per-run names ran past the width of the window.
 */
const OPTION_CHARS = 48;
const clip = (text: string) => (text.length > OPTION_CHARS ? `${text.slice(0, OPTION_CHARS - 1)}…` : text);

/** Rows in the recent-runs list. The chart still counts every job in the window. */
const LIST_ROWS = 100;

/** A clicked bar's slot, narrowing the whole tab to it. */
interface Focus {
  start: number;
  end: number;
  label: string;
}

export function RunHistory({
  load,
  onAnalyse,
}: {
  load: (hours: number) => Promise<RunsFeed>;
  /** Opens the Job analysis tab on a job, from its details dialog. */
  onAnalyse?: (job: RunJob) => void;
}) {
  const [rangeKey, setRangeKey] = useState('1d');
  const range = rangeOf(rangeKey);
  // Bars by default, and not remembered: the screen always opens on the counts.
  const [view, setView] = useState<View>('bars');
  const [filter, setFilter] = useState<RunFilter>(NO_FILTER);
  const [focus, setFocus] = useState<Focus | null>(null);
  const [details, setDetails] = useState<RunJob | null>(null);
  const { state } = useAsync(() => load(range.hours), [range.hours]);

  // A refetch keeps the previous frame on screen, faded, rather than blanking
  // the chart to a loading state every time the range changes.
  const last = useRef<RunsFeed | null>(null);
  if (state.status === 'ready') last.current = state.data;
  const feed = state.status === 'ready' ? state.data : last.current;
  const refreshing = state.status === 'loading' && feed !== null;

  const filtered = useMemo(() => (feed ? filterJobs(feed.jobs, filter) : []), [feed, filter]);
  const options = useMemo(() => (feed ? filterOptions(feed.jobs, filter) : { repos: [], workflows: [] }), [feed, filter]);
  const now = feed ? Date.parse(feed.until) : Date.now();

  // The window on screen: the chosen range, or the slot a bar click narrowed it to.
  const shown = focus ? focusRange(focus.start, focus.end) : range;
  const start = focus ? focus.start : now - range.hours * 3_600_000;
  const end = focus ? focus.end : now;
  const jobs = useMemo(() => (focus ? inWindow(filtered, focus.start, focus.end) : filtered), [filtered, focus]);
  const slots = useMemo(
    () =>
      focus
        ? slotsFor(jobs, focusRange(focus.start, focus.end), focus.end - 1, undefined, focus.start)
        : slotsFor(jobs, range, now),
    [jobs, focus, range, now],
  );

  const narrowTo = (slot: Slot) => {
    if (slot.total === 0) return;
    setFocus({
      start: slot.start,
      end: slot.end,
      label: shown.slotMinutes >= 1440 ? dayFmt.format(slot.start) : slotWindow(slot, shown),
    });
  };

  return (
    // `minmax(0, 1fr)`, not the default `auto` track: a grid item's minimum is
    // its content, so one long job name in the list widened the whole column and
    // scaled the chart up four times (seen on 30D with keyodo's contract jobs).
    <div style={s.column}>
      {/* One filter row, above everything it scopes: the chart and the list agree. */}
      <div role="group" aria-label="Run history filters" style={s.filters}>
        <Segmented
          label="Time range"
          value={rangeKey}
          onChange={(key) => {
            // Any range, even the one already chosen, clears a narrowed slot.
            setRangeKey(key);
            setFocus(null);
          }}
          options={RANGES.map((r) => ({ value: r.key, label: r.label }))}
        />
        <Select
          size="sm"
          label="Repository"
          value={filter.repo}
          onChange={(v) => setFilter({ ...filter, repo: v, workflow: '' })}
          options={[{ value: '', label: 'All repos' }, ...options.repos.map((r) => ({ value: r, label: r }))]}
        />
        <Select
          size="sm"
          label="Workflow"
          value={filter.workflow}
          onChange={(v) => setFilter({ ...filter, workflow: v })}
          options={[{ value: '', label: 'All workflows' }, ...options.workflows.map((w) => ({ value: w, label: clip(w) }))]}
        />
        <Select
          size="sm"
          label="Runner type"
          value={filter.kind}
          onChange={(v) => setFilter({ ...filter, kind: v })}
          options={[
            { value: '', label: 'All runner types' },
            ...Object.entries(KIND_LABEL).map(([kind, label]) => ({ value: kind, label })),
          ]}
        />
        {focus ? (
          <button type="button" onClick={() => setFocus(null)} style={s.chip} aria-label={`Showing ${focus.label} only. Clear.`}>
            {focus.label}
            <span aria-hidden style={{ color: C.muted }}>
              ✕
            </span>
          </button>
        ) : null}
      </div>

      {state.status === 'failed' ? <Note tone="danger">{state.message}</Note> : null}
      {feed?.truncated ? (
        <Note tone="warn">
          This range holds more jobs than fleet-api sends at once, so the oldest are missing. Pick a shorter range for all of them.
        </Note>
      ) : null}

      {!feed ? (
        state.status === 'loading' ? (
          <Panel title="Run history">
            <p style={s.loading}>Loading run history…</p>
          </Panel>
        ) : null
      ) : (
        <div style={{ ...s.column, opacity: refreshing ? 0.55 : 1, transition: 'opacity .15s ease' }}>
          <Panel
            title="Run history"
            action={
              <Segmented
                label="Chart"
                value={view}
                onChange={(v) => setView(v as View)}
                options={[
                  { value: 'bars', label: 'Bars' },
                  { value: 'scatter', label: 'Scatter' },
                ]}
              />
            }
          >
            {jobs.length === 0 ? (
              <EmptyState title="No jobs in this range" suggestion="Pick a longer range, or clear a filter." />
            ) : (
              <div style={{ padding: '1rem 1.2rem', display: 'grid', gap: '.8rem' }}>
                {view === 'bars' ? (
                  <BarsChart slots={slots} range={shown} onSelect={narrowTo} />
                ) : (
                  <ScatterChart
                    jobs={jobs}
                    range={shown}
                    start={start}
                    end={end}
                    boundaries={slots.map((slot) => slot.start)}
                    onSelect={setDetails}
                  />
                )}
                <Legend jobs={jobs} view={view} />
              </div>
            )}
          </Panel>
          <RecentRuns jobs={jobs} onDetails={setDetails} />
        </div>
      )}

      {details ? <JobDetails job={details} onClose={() => setDetails(null)} onAnalyse={onAnalyse} /> : null}
    </div>
  );
}

// ── CONTROLS ─────────────────────────────────────────────────────────

function Segmented({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  return (
    <div role="radiogroup" aria-label={label} style={s.segmented}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.value)}
            style={{
              ...s.segment,
              background: on ? C.blueTint : 'transparent',
              color: on ? C.blueText : C.textMid,
              fontWeight: on ? 640 : 500,
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ── CHARTS ───────────────────────────────────────────────────────────

/**
 * Both charts draw in CSS pixels: the viewBox is the measured width of the
 * panel, not a fixed 720-wide box stretched to fit it. Stretched, the 10px axis
 * text rendered near 22px on a wide screen and the chart grew taller with it
 * (RidgeWill/org-admin#119). Only the width follows the panel; the height is fixed.
 */
const H = 220;
const M = { top: 10, right: 10, bottom: 26, left: 44 };
const PLOT_H = H - M.top - M.bottom;
/** Drawn until the panel is measured. */
const FALLBACK_W = 720;
/** Room for one x-axis label ("14 Sep, 09:00 AM" at 10px) and the gap after it. */
const X_LABEL_SPACE = 110;

/** A chart's width in CSS pixels, following its container as the window resizes. */
function useChartWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(FALLBACK_W);
  // A layout effect, so a narrow panel is measured before the first paint rather than flashing a 720px chart.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      if (el.clientWidth > 0) setWidth(el.clientWidth);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return { ref, width };
}

/** How many x-axis labels fit: six on a wide panel as before, fewer on a narrow one, never under two. */
const xLabelSlots = (plotW: number) => Math.max(2, Math.min(6, Math.floor(plotW / X_LABEL_SPACE)));

const timeFmt = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' });
const dayFmt = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' });
const dayTimeFmt = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/** Axis labels: times within a day, day and time over two, days beyond. */
function axisLabel(ms: number, range: Range): string {
  if (range.hours <= 24) return timeFmt.format(ms);
  if (range.hours <= 48) return dayTimeFmt.format(ms);
  return dayFmt.format(ms);
}

/** A bar with its data end rounded and its baseline end square. */
function roundedTop(x: number, y: number, w: number, h: number): string {
  if (h <= 0) return '';
  const r = Math.max(0, Math.min(4, h, w / 2));
  return `M${x},${y + h} L${x},${y + r} Q${x},${y} ${x + r},${y} L${x + w - r},${y} Q${x + w},${y} ${x + w},${y + r} L${x + w},${y + h} Z`;
}

/** Where a tooltip points, in viewport pixels: just above this spot. */
interface Anchor {
  x: number;
  y: number;
}

/**
 * Clears a hover when anything scrolls. The tooltip is fixed to the viewport,
 * and the portal's main frame scrolls under it, so without this it would float
 * over whatever slid into its place.
 */
function useClearOnScroll(active: boolean, clear: () => void) {
  useEffect(() => {
    if (!active) return;
    window.addEventListener('scroll', clear, true);
    return () => window.removeEventListener('scroll', clear, true);
  }, [active, clear]);
}

function BarsChart({ slots, range, onSelect }: { slots: Slot[]; range: Range; onSelect: (slot: Slot) => void }) {
  const [hover, setHover] = useState<{ index: number; at: Anchor } | null>(null);
  const clear = useMemo(() => () => setHover(null), []);
  useClearOnScroll(hover !== null, clear);

  const ticks = countTicks(Math.max(0, ...slots.map((slot) => slot.total)));
  const top = ticks[ticks.length - 1]!;
  const { ref, width: w } = useChartWidth();
  const plotW = w - M.left - M.right;
  const band = plotW / Math.max(1, slots.length);
  // Capped at 24 and never filling the slot: the leftover band is the gap between bars.
  const barW = Math.max(2, Math.min(24, band - 3));
  const y = (v: number) => M.top + PLOT_H - (v / top) * PLOT_H;
  const labelEvery = Math.ceil(slots.length / xLabelSlots(plotW));
  const hovered = hover === null ? undefined : slots[hover.index];

  const onKey = (e: KeyboardEvent, slot: Slot) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(slot);
    }
  };

  return (
    <div ref={ref} style={{ position: 'relative' }} onPointerLeave={clear}>
      <svg viewBox={`0 0 ${w} ${H}`} role="group" aria-label={`Jobs per time slot by outcome, ${range.label}`} style={s.svg}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={M.left} x2={w - M.right} y1={y(t)} y2={y(t)} stroke={C.border} strokeWidth={1} />
            <text x={M.left - 8} y={y(t)} dy="0.32em" textAnchor="end" fontSize={10} fill={C.muted} style={numeric}>
              {count(t)}
            </text>
          </g>
        ))}
        {slots.map((slot, i) => {
          const x0 = M.left + i * band;
          const cx = x0 + band / 2;
          const present = OUTCOMES.filter((o) => slot.counts[o.key] > 0);
          let base = 0;
          return (
            <g key={slot.start}>
              {hover?.index === i ? <rect x={x0} y={M.top} width={band} height={PLOT_H} fill={C.border3} /> : null}
              {present.map((o, k) => {
                const v = slot.counts[o.key];
                const yTop = y(base + v);
                // The 2px surface gap sits under every segment but the first.
                const h = Math.max(0, y(base) - yTop - (k === 0 ? 0 : 2));
                base += v;
                return k === present.length - 1 ? (
                  <path key={o.key} d={roundedTop(cx - barW / 2, yTop, barW, h)} fill={COLOR[o.key]} />
                ) : (
                  <rect key={o.key} x={cx - barW / 2} y={yTop} width={barW} height={h} fill={COLOR[o.key]} />
                );
              })}
              {i % labelEvery === 0 ? (
                <text x={cx} y={H - 8} textAnchor="middle" fontSize={10} fill={C.muted}>
                  {axisLabel(slot.start, range)}
                </text>
              ) : null}
              {/* The whole slot is the hit target, not the painted bar. */}
              <rect
                x={x0}
                y={M.top}
                width={band}
                height={PLOT_H}
                fill="transparent"
                role="button"
                tabIndex={slot.total > 0 ? 0 : -1}
                aria-label={`${slotWindow(slot, range)}: ${slot.total} job${slot.total === 1 ? '' : 's'}. Show only this time.`}
                style={{ cursor: slot.total > 0 ? 'pointer' : 'default', outline: 'none' }}
                onPointerMove={(e) => setHover({ index: i, at: { x: e.clientX, y: e.clientY } })}
                onFocus={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  setHover({ index: i, at: { x: r.left + r.width / 2, y: r.top } });
                }}
                onBlur={clear}
                onClick={() => onSelect(slot)}
                onKeyDown={(e) => onKey(e, slot)}
              />
            </g>
          );
        })}
      </svg>
      {hovered && hover ? (
        <Tooltip at={hover.at}>
          <div style={s.tipTitle}>{slotWindow(hovered, range)}</div>
          {/* Only the outcomes this slot has: a column of zeros is noise. */}
          {OUTCOMES.filter((o) => hovered.counts[o.key] > 0).map((o) => (
            <TipRow key={o.key} color={COLOR[o.key]} value={count(hovered.counts[o.key])} label={o.label} />
          ))}
          {hovered.total === 0 ? <div style={s.tipText}>No jobs</div> : null}
          <div style={s.tipRule} />
          <TipRow value={seconds(hovered.medianDuration_s)} label="median run time" />
          <TipRow value={seconds(hovered.medianQueue_s)} label="median wait" />
          {hovered.total > 0 ? <div style={s.tipHint}>Click to show only this {range.slotMinutes >= 1440 ? 'day' : 'time'}</div> : null}
        </Tooltip>
      ) : null}
    </div>
  );
}

function slotWindow(slot: Slot, range: Range): string {
  const startLabel = range.hours <= 24 ? `${dayFmt.format(slot.start)}, ${timeFmt.format(slot.start)}` : dayTimeFmt.format(slot.start);
  return `${startLabel} to ${range.slotMinutes >= 1440 ? dayTimeFmt.format(slot.end) : timeFmt.format(slot.end)}`;
}

/**
 * A scatter mark per outcome. Shape carries the outcome as well as colour: a
 * dot, a cross, a ring and a square.
 *
 * Small on purpose (Jon, 13 Sep: "so you can see more in tight areas"). A 5px
 * mark is below the usual 8px, which is why hovering picks the NEAREST point
 * within 24px rather than asking anyone to land on one.
 */
function Mark({ outcome, cx, cy, size = 1, color }: { outcome: Outcome; cx: number; cy: number; size?: number; color?: string }) {
  const r = 2.5 * size;
  const stroke = 1.25 * size;
  const paint = color ?? COLOR[outcome];
  switch (outcome) {
    case 'failure':
      return (
        <path
          d={`M${cx - r},${cy - r} L${cx + r},${cy + r} M${cx + r},${cy - r} L${cx - r},${cy + r}`}
          stroke={paint}
          strokeWidth={stroke + 0.25}
          strokeLinecap="round"
        />
      );
    case 'cancelled':
      return <circle cx={cx} cy={cy} r={r} fill="none" stroke={paint} strokeWidth={stroke} />;
    case 'skipped':
      return <rect x={cx - r} y={cy - r} width={r * 2} height={r * 2} fill="none" stroke={paint} strokeWidth={stroke} />;
    default:
      return <circle cx={cx} cy={cy} r={r} fill={paint} />;
  }
}

/** A little smaller again once the plot is crowded. */
const markSize = (points: number) => (points > 1500 ? 0.8 : points > 500 ? 0.9 : 1);

/** Drawn in this order so a failure is never hidden under a success. */
const SCATTER_ORDER: Outcome[] = ['success', 'skipped', 'cancelled', 'failure'];

function ScatterChart({
  jobs,
  range,
  start,
  end,
  boundaries,
  onSelect,
}: {
  jobs: RunJob[];
  range: Range;
  start: number;
  end: number;
  /** The bars' slot starts, so both views label the same round clock times. */
  boundaries: number[];
  onSelect: (job: RunJob) => void;
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [hover, setHover] = useState<{ index: number; at: Anchor } | null>(null);
  const clear = useMemo(() => () => setHover(null), []);
  useClearOnScroll(hover !== null, clear);

  const { ref, width: w } = useChartWidth();
  const plotW = w - M.left - M.right;

  // Finished jobs only: a running job has no run time to plot yet. The bars count it.
  const points = useMemo(() => {
    const finished = jobs.filter((j) => j.status === 'completed' && j.duration_s !== null);
    const axis = scatterAxis(finished.map((j) => j.duration_s!));
    const placed = finished.map((job) => ({
      job,
      outcome: outcomeOf(job),
      // Above the axis: pinned to the top edge and marked, never off the plot.
      clipped: job.duration_s! > axis.top,
      x: M.left + ((jobTime(job) - start) / (end - start)) * plotW,
      y: M.top + PLOT_H - (Math.min(job.duration_s!, axis.top) / axis.top) * PLOT_H,
    }));
    return { ...axis, placed };
  }, [jobs, start, end, plotW]);

  const inside = boundaries.filter((t) => t >= start);
  const every = Math.ceil(inside.length / xLabelSlots(plotW));
  const xTicks = inside.filter((_, i) => i % every === 0);
  const size = markSize(points.placed.length);

  /** The nearest point within 24px of the pointer, and where its tooltip goes. */
  const nearest = (e: PointerEvent<SVGSVGElement> | React.MouseEvent<SVGSVGElement>) => {
    const box = svgRef.current?.getBoundingClientRect();
    if (!box || box.width === 0) return null;
    // 1 once measured; not assumed, because a resize can land between a frame and its measurement.
    const scale = w / box.width;
    const px = (e.clientX - box.left) * scale;
    const py = (e.clientY - box.top) * scale;
    let best = -1;
    let bestD = (24 * scale) ** 2;
    points.placed.forEach((p, i) => {
      const d = (p.x - px) ** 2 + (p.y - py) ** 2;
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    });
    const p = points.placed[best];
    if (!p) return null;
    // Above whichever is higher, the dot or the pointer, so neither is covered.
    const dotY = box.top + p.y / scale;
    return { index: best, at: { x: box.left + p.x / scale, y: Math.min(dotY - 6, e.clientY) } };
  };

  const hovered = hover === null ? undefined : points.placed[hover.index];

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${w} ${H}`}
        role="img"
        aria-label={`Run time of each finished job, ${range.label}. Click a job for its details; the list below has every one.`}
        style={{ ...s.svg, cursor: hovered ? 'pointer' : 'default' }}
        onPointerMove={(e) => setHover(nearest(e))}
        onPointerLeave={clear}
        onClick={(e) => {
          const hit = nearest(e);
          const job = hit ? points.placed[hit.index]?.job : undefined;
          if (job) onSelect(job);
        }}
      >
        {points.ticks.map((t) => {
          const y = M.top + PLOT_H - (t / points.top) * PLOT_H;
          return (
            <g key={t}>
              <line x1={M.left} x2={w - M.right} y1={y} y2={y} stroke={C.border} strokeWidth={1} />
              <text x={M.left - 8} y={y} dy="0.32em" textAnchor="end" fontSize={10} fill={C.muted} style={numeric}>
                {durationLabel(t)}
              </text>
            </g>
          );
        })}
        {xTicks.map((t) => {
          const x = M.left + ((t - start) / (end - start)) * plotW;
          // A label near either edge is anchored inward, so it is never clipped.
          const anchor = x > w - M.right - 40 ? 'end' : x < M.left + 40 ? 'start' : 'middle';
          return (
            <text key={t} x={x} y={H - 8} textAnchor={anchor} fontSize={10} fill={C.muted}>
              {axisLabel(t, range)}
            </text>
          );
        })}
        {SCATTER_ORDER.flatMap((outcome) =>
          points.placed
            .filter((p) => p.outcome === outcome)
            .map((p) => (
              <g key={p.job.id}>
                <Mark outcome={p.outcome} cx={p.x} cy={p.y} size={size} />
                {p.clipped ? (
                  <path d={`M${p.x - 2},${p.y - 5} L${p.x},${p.y - 7} L${p.x + 2},${p.y - 5}`} fill="none" stroke={C.textMid2} strokeWidth={1} strokeLinecap="round" strokeLinejoin="round" />
                ) : null}
              </g>
            )),
        )}
        {/* The hovered job, drawn again on top in its own colour, larger, with a surface ring to lift it out of a crowd. */}
        {hovered ? (
          <g>
            <circle cx={hovered.x} cy={hovered.y} r={5} fill={C.surface} />
            <Mark outcome={hovered.outcome === 'failure' ? 'failure' : 'success'} cx={hovered.x} cy={hovered.y} size={1.6} color={HIGHLIGHT} />
          </g>
        ) : null}
      </svg>
      {points.above > 0 ? (
        <p style={{ ...s.note, marginTop: '.35rem' }}>
          {count(points.above)} slower than {durationLabel(points.top)}, shown on the top edge.
        </p>
      ) : null}
      {hovered && hover ? (
        <Tooltip at={hover.at}>
          <div style={s.tipTitle}>
            {hovered.job.workflow} · {hovered.job.job_name}
          </div>
          <TipRow color={COLOR[hovered.outcome]} value={seconds(hovered.job.duration_s)} label="run time" />
          <TipRow value={seconds(hovered.job.queue_s)} label="wait" />
          <div style={s.tipRule} />
          <div style={s.tipText}>
            {labelOf(hovered.outcome)} · {dayTimeFmt.format(jobTime(hovered.job))}
          </div>
          <div style={s.tipText}>
            {hovered.job.repo}
            {hovered.job.head_branch ? ` · ${hovered.job.head_branch}` : ''}
          </div>
          <div style={s.tipHint}>Click for details</div>
        </Tooltip>
      ) : null}
    </div>
  );
}

const TIP_WIDTH = 230;
/** The tooltip's distance from the window's edges. */
const TIP_EDGE = 8;

/**
 * Just above the pointer, centred on it, and inside the window at both edges.
 * Below the pointer when the window's top edge would cut it off (Jon, 14 Sep).
 *
 * The flip used to fire within a fixed 190px of the top, a guess at a bar
 * tooltip's height. A scatter tooltip with a long workflow name was twice that,
 * so it opened above the pointer and ran off the top of the window. Now its real
 * height is measured before it is painted.
 *
 * Portalled to the body and fixed to the viewport: a panel's `overflow: hidden`
 * would clip it, and above the chart is exactly where it now goes.
 */
function Tooltip({ at, children }: { at: Anchor; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const left = Math.max(TIP_EDGE, Math.min(at.x - TIP_WIDTH / 2, window.innerWidth - TIP_WIDTH - TIP_EDGE));
  // No dependency list: the anchor and the content both change with every hover.
  // `top` is set here only, never in `style`, so a re-render cannot undo it.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const height = el.offsetHeight;
    const above = at.y - 12 - height;
    const top = above >= TIP_EDGE ? above : Math.min(at.y + 20, window.innerHeight - height - TIP_EDGE);
    el.style.top = `${Math.max(TIP_EDGE, top)}px`;
  });
  return createPortal(
    <div ref={ref} role="tooltip" style={{ ...s.tooltip, left }}>
      {children}
    </div>,
    document.body,
  );
}

/** Value first and strong, label after it: the reader has the series and wants the number. */
function TipRow({ value, label, color }: { value: string; label: string; color?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '.45rem', fontSize: '.78rem' }}>
      {color ? <span aria-hidden style={{ width: 10, height: 2, borderRadius: 1, background: color, flex: 'none' }} /> : null}
      <strong style={{ ...numeric, color: C.text }}>{value}</strong>
      <span style={{ color: C.textMid2 }}>{label}</span>
    </div>
  );
}

/**
 * One entry per outcome present in the filtered window, in stacking order.
 *
 * An outcome with no jobs is left out rather than listed at 0, and running jobs
 * are left off the scatter's legend because the scatter cannot plot them.
 */
function Legend({ jobs, view }: { jobs: RunJob[]; view: View }) {
  const totals = OUTCOMES.map((o) => ({ ...o, n: jobs.filter((j) => outcomeOf(j) === o.key).length })).filter(
    (o) => o.n > 0 && (view === 'bars' || o.key !== 'running'),
  );
  return (
    <ul style={s.legend} aria-label="Legend">
      {totals.map((o) => (
        <li key={o.key} style={s.legendItem}>
          <svg width={12} height={12} viewBox="0 0 12 12" aria-hidden style={{ flex: 'none' }}>
            {view === 'bars' ? (
              <rect x={1} y={1} width={10} height={10} rx={2.5} fill={COLOR[o.key]} />
            ) : (
              <Mark outcome={o.key} cx={6} cy={6} size={1.6} />
            )}
          </svg>
          <span style={{ color: C.textMid }}>{o.label}</span>
          <span style={{ ...numeric, color: C.muted }}>{count(o.n)}</span>
        </li>
      ))}
    </ul>
  );
}

// ── RECENT RUNS ──────────────────────────────────────────────────────

function RecentRuns({ jobs, onDetails }: { jobs: RunJob[]; onDetails: (job: RunJob) => void }) {
  const rows = [...jobs].sort((a, b) => jobTime(b) - jobTime(a)).slice(0, LIST_ROWS);
  return (
    <Panel title="Recent runs">
      {rows.length === 0 ? (
        <EmptyState title="No jobs in this range" />
      ) : (
        <>
          <Table columns={['Outcome', 'Job', 'Repo and branch', 'Wait', 'Run time', '']} actionsLast>
            {rows.map((job) => (
              /**
               * Double-click opens the details, as on the Activity screen. It is
               * not the only way in: a double-click is invisible to anyone who
               * does not already know it, so the menu at the end of the row is
               * the discoverable half, and the one a keyboard reaches.
               */
              <tr
                key={job.id}
                className="rw-rowhover"
                onDoubleClick={(ev) => {
                  // Not when the double-click landed on the row's menu.
                  if ((ev.target as HTMLElement).closest('button, a')) return;
                  onDetails(job);
                }}
              >
                <Td>
                  <OutcomeLabel outcome={outcomeOf(job)} />
                </Td>
                <Td>
                  <div style={s.wrap} title={`${job.workflow} · ${job.job_name}`}>
                    {job.workflow} · {job.job_name}
                  </div>
                </Td>
                <Td>
                  <RepoBranch repo={job.repo} branch={job.head_branch} />
                </Td>
                <Td>
                  <span style={numeric}>{seconds(job.queue_s)}</span>
                </Td>
                <Td>
                  <span style={numeric}>{seconds(job.duration_s)}</span>
                </Td>
                <Td>
                  <Menu label={`Options for ${job.workflow} · ${job.job_name}`}>
                    {(close) => (
                      <>
                        <MenuItem
                          onClick={() => {
                            close();
                            onDetails(job);
                          }}
                        >
                          Show details
                        </MenuItem>
                        <MenuItem href={jobUrl(job)} external onClick={close}>
                          Open on GitHub
                        </MenuItem>
                      </>
                    )}
                  </Menu>
                </Td>
              </tr>
            ))}
          </Table>
          {jobs.length > rows.length ? (
            <p style={{ ...s.note, padding: '.7rem 1.2rem' }}>
              Latest {count(rows.length)} of {count(jobs.length)}.
            </p>
          ) : null}
        </>
      )}
    </Panel>
  );
}

const s = {
  column: { display: 'grid', gap: '1rem', gridTemplateColumns: 'minmax(0, 1fr)' },
  /**
   * Long job names (keyodo's contract matrix) wrap inside their column instead
   * of widening the table. `break-word`, not `anywhere`: `anywhere` lets the
   * column's minimum shrink to one letter, and beside a no-wrap branch pill the
   * table took it up on that ("tes / t · / tes / t").
   */
  wrap: { minWidth: '9rem', maxWidth: 340, overflowWrap: 'break-word' as const },
  filters: { display: 'flex', flexWrap: 'wrap' as const, gap: '.6rem', alignItems: 'center' },
  // Capped: a select is as wide as its longest option, and one long workflow
  // name made the page three screens wide.
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '.45rem',
    font: 'inherit',
    fontSize: '.8rem',
    fontWeight: 600,
    padding: '.28rem .6rem',
    borderRadius: 999,
    border: `1px solid ${C.border}`,
    background: C.blueTint,
    color: C.blueText,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
  },
  segmented: {
    display: 'inline-flex',
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    overflow: 'hidden',
    background: C.surface,
  },
  segment: {
    font: 'inherit',
    fontSize: '.8rem',
    padding: '.3rem .65rem',
    border: 'none',
    cursor: 'pointer',
  },
  // `width: 100%` rather than the measured width, so the SVG never holds its panel open while the window narrows.
  svg: { width: '100%', height: H, display: 'block', touchAction: 'none' as const },
  legend: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '.4rem 1.25rem',
    margin: 0,
    padding: 0,
    listStyle: 'none',
    fontSize: '.8rem',
  },
  legendItem: { display: 'inline-flex', alignItems: 'center', gap: '.4rem' },
  tooltip: {
    position: 'fixed' as const,
    width: TIP_WIDTH,
    display: 'grid',
    gap: '.25rem',
    padding: '.55rem .7rem',
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    boxShadow: '0 6px 20px rgba(16, 22, 38, .16)',
    pointerEvents: 'none' as const,
    // Under a dialog (40), over everything else.
    zIndex: 30,
  },
  // Three lines at most: a job's name is GitHub's to choose, and the tooltip must still fit the window.
  tipTitle: {
    fontSize: '.78rem',
    fontWeight: 640,
    color: C.text,
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical' as const,
    overflow: 'hidden',
  },
  tipText: { fontSize: '.75rem', color: C.textMid2 },
  tipHint: { fontSize: '.72rem', color: C.muted, marginTop: '.1rem' },
  tipRule: { height: 1, background: C.border2, margin: '.15rem 0' },
  note: { margin: 0, fontSize: '.78rem', color: C.muted, lineHeight: 1.5 },
  loading: { margin: 0, padding: '1rem 1.2rem', color: C.muted },
};
